import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Circle,
  Clock,
  CreditCard,
  GraduationCap,
  ReceiptText,
  X,
} from "lucide-react";
import React, { useEffect, useState, useCallback } from "react";
import { TailSpin } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createOrder,
  getShoppingCartItems,
} from "../../api/action/cart";
import { findOrders } from "../../api/action/order";
import { setOrder } from "../../api/reducers/orderReducer";
import Loading from "../LandingPage/InstitutionDomain/Enquiry/components/Loading";
import institutionTheme from "../LandingPage/InstitutionDomain/components/theme/theme1";
import { FormatDate } from "../../components/FormatDate";
import { motion } from "framer-motion";
import { Alert as UiAlert } from "../../components/ui/Feedback";

const paymentTheme = {
  fontFamily: institutionTheme.fontFamily || "Georgia, serif",
  palette: {
    primary: {
      main: institutionTheme.headerBg || "#b5a262",
      light: institutionTheme.footerBg || "#f8f4e8",
      dark: institutionTheme.titleText || "#13294b",
    },
    text: {
      primary: institutionTheme.titleText || "#13294b",
      secondary: institutionTheme.textMuted || "#4a4a4a",
    },
    grey: {
      50: "#f9fafb",
      100: "#f3f4f6",
      200: "#e5e7eb",
      400: "#9ca3af",
    },
    action: {
      hover: "#f3f4f6",
    },
    success: {
      main: "#059669",
    },
    divider: "#e5e7eb",
  },
  shadows: [
    "none",
    "0 1px 2px rgba(15,23,42,0.08)",
    "0 4px 12px rgba(15,23,42,0.12)",
    "0 8px 24px rgba(15,23,42,0.14)",
    "0 12px 32px rgba(15,23,42,0.16)",
    "0 16px 40px rgba(15,23,42,0.18)",
    "0 20px 48px rgba(15,23,42,0.2)",
  ],
  zIndex: {
    drawer: 1200,
  },
};

const useTheme = () => paymentTheme;

const spacingValue = (value) => (typeof value === "number" ? `${value * 8}px` : value);

const sxToStyle = (sx = {}) => {
  const source = typeof sx === "function" ? sx(paymentTheme) : sx;
  const style = {};
  Object.entries(source || {}).forEach(([key, value]) => {
    if (key.startsWith("&")) return;
    if (key === "p") style.padding = spacingValue(value);
    else if (key === "px") {
      style.paddingLeft = spacingValue(value);
      style.paddingRight = spacingValue(value);
    } else if (key === "py") {
      style.paddingTop = spacingValue(value);
      style.paddingBottom = spacingValue(value);
    } else if (key === "pt") style.paddingTop = spacingValue(value);
    else if (key === "pb") style.paddingBottom = spacingValue(value);
    else if (key === "pl") style.paddingLeft = spacingValue(value);
    else if (key === "pr") style.paddingRight = spacingValue(value);
    else if (key === "m") style.margin = spacingValue(value);
    else if (key === "mx") {
      style.marginLeft = spacingValue(value);
      style.marginRight = spacingValue(value);
    } else if (key === "my") {
      style.marginTop = spacingValue(value);
      style.marginBottom = spacingValue(value);
    } else if (key === "mt") style.marginTop = spacingValue(value);
    else if (key === "mb") style.marginBottom = spacingValue(value);
    else if (key === "ml") style.marginLeft = spacingValue(value);
    else if (key === "mr") style.marginRight = spacingValue(value);
    else if (key === "bgcolor") style.backgroundColor = value;
    else if (key === "gap") style.gap = spacingValue(value);
    else style[key] = value;
  });
  return style;
};

const mergeStyles = (sx, style) => ({ ...sxToStyle(sx), ...(style || {}) });

const iconStyle = (sx, style, fontSize) => ({
  width: fontSize === "small" ? 16 : undefined,
  height: fontSize === "small" ? 16 : undefined,
  ...mergeStyles(sx, style),
});

const createIcon = (Icon) => ({ sx, style, fontSize, ...props }) => (
  <Icon style={iconStyle(sx, style, fontSize)} {...props} />
);

const ArrowBackIosNew = createIcon(ChevronLeft);
const School = createIcon(GraduationCap);
const Receipt = createIcon(ReceiptText);
const Payment = createIcon(CreditCard);
const LibraryBooks = createIcon(BookOpen);
const CheckCircle = createIcon(CheckCircle2);
const ExpandMore = createIcon(ChevronDown);
const ExpandLess = createIcon(ChevronUp);
const RadioButtonUnchecked = createIcon(Circle);
const CalendarToday = createIcon(CalendarDays);
const Schedule = createIcon(Clock);

const Box = ({
  children,
  sx,
  style,
  display,
  alignItems,
  justifyContent,
  flexWrap,
  mt,
  mr,
  mb,
  width,
  component: Component = "div",
  ...props
}) => (
  <Component
    style={{
      display,
      alignItems,
      justifyContent,
      flexWrap,
      marginTop: spacingValue(mt),
      marginRight: spacingValue(mr),
      marginBottom: spacingValue(mb),
      width: spacingValue(width),
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {children}
  </Component>
);

const Container = ({ children, maxWidth = "lg", sx, style, ...props }) => {
  const widths = { lg: "1024px", xl: "1280px" };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: widths[maxWidth] || widths.lg,
        marginLeft: "auto",
        marginRight: "auto",
        paddingLeft: "16px",
        paddingRight: "16px",
        ...mergeStyles(sx, style),
      }}
      {...props}
    >
      {children}
    </div>
  );
};

const Grid = ({ children, container, item, spacing = 0, xs = 12, md, sx, style, ...props }) => {
  if (container) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          gap: spacingValue(spacing),
          ...mergeStyles(sx, style),
        }}
        {...props}
      >
        {children}
      </div>
    );
  }

  const span = md || xs || 12;
  return <div style={{ gridColumn: `span ${span} / span ${span}`, ...mergeStyles(sx, style) }} {...props}>{children}</div>;
};

const Typography = ({ children, variant, sx, style, fontWeight, color, mr, ...props }) => {
  const Component = variant?.startsWith?.("h") ? variant : variant === "caption" ? "span" : "p";
  return (
    <Component
      style={{
        fontWeight,
        color: color === "textSecondary" ? paymentTheme.palette.text.secondary : color,
        marginRight: spacingValue(mr),
        ...mergeStyles(sx, style),
      }}
      {...props}
    >
      {children}
    </Component>
  );
};

const Button = ({ children, fullWidth, startIcon, endIcon, sx, style, type = "button", variant, color, size, ...props }) => (
  <button
    type={type}
    style={{
      width: fullWidth ? "100%" : undefined,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      border: variant === "outlined" ? `1px solid ${paymentTheme.palette.primary.main}` : "1px solid transparent",
      backgroundColor: variant === "contained" ? paymentTheme.palette.primary.main : paymentTheme.palette.primary.light,
      color: variant === "contained" ? "#fff" : paymentTheme.palette.primary.main,
      cursor: props.disabled ? "not-allowed" : "pointer",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {startIcon}
    {children}
    {endIcon}
  </button>
);

const IconButton = ({ children, sx, style, type = "button", size, ...props }) => (
  <button
    type={type}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: 0,
      background: "transparent",
      cursor: "pointer",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {children}
  </button>
);

const Checkbox = ({ icon, checkedIcon, sx, style, color, size, ...props }) => (
  <input type="checkbox" style={mergeStyles(sx, style)} {...props} />
);

const Divider = ({ sx, style }) => <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", ...mergeStyles(sx, style) }} />;

const Card = ({ children, sx, style, elevation, variant, ...props }) => (
  <div
    style={{
      backgroundColor: "#fff",
      boxShadow: variant === "outlined" ? "none" : paymentTheme.shadows[2],
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {children}
  </div>
);

const CardContent = ({ children, sx, style, ...props }) => <div style={mergeStyles(sx, style)} {...props}>{children}</div>;

const Chip = ({ label, sx, style, size, ...props }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "999px",
      padding: "2px 8px",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {label}
  </span>
);

const TableContainer = ({ children, sx, style, component, ...props }) => (
  <div style={{ overflowX: "auto", ...mergeStyles(sx, style) }} {...props}>{children}</div>
);
const Table = ({ children, sx, style, size, ...props }) => <table style={{ width: "100%", borderCollapse: "collapse", ...mergeStyles(sx, style) }} {...props}>{children}</table>;
const TableHead = ({ children }) => <thead>{children}</thead>;
const TableBody = ({ children }) => <tbody>{children}</tbody>;
const TableRow = ({ children, sx, style, hover, ...props }) => <tr style={mergeStyles(sx, style)} {...props}>{children}</tr>;
const TableCell = ({ children, sx, style, align, colSpan, ...props }) => (
  <td
    colSpan={colSpan}
    style={{
      padding: "12px",
      textAlign: align,
      borderBottom: "1px solid #e5e7eb",
      ...mergeStyles(sx, style),
    }}
    {...props}
  >
    {children}
  </td>
);

const Collapse = ({ children, in: isOpen }) => (isOpen ? <>{children}</> : null);

const Avatar = ({ children, src, alt, sx, style }) => (
  src ? (
    <img src={src} alt={alt} style={{ borderRadius: "999px", objectFit: "cover", ...mergeStyles(sx, style) }} />
  ) : (
    <div style={{ borderRadius: "999px", display: "inline-flex", alignItems: "center", justifyContent: "center", ...mergeStyles(sx, style) }}>
      {children}
    </div>
  )
);

const Backdrop = ({ children, open, sx, style }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" style={mergeStyles(sx, style)}>
      {children}
    </div>
  );
};

const Snackbar = ({ children, open }) => {
  if (!open) return null;
  return (
    <div className="fixed bottom-5 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
      {children}
    </div>
  );
};

const Alert = ({ children, severity = "info", onClose }) => (
  <div className="relative">
    <UiAlert tone={severity}>{children}</UiAlert>
    {onClose && (
      <button type="button" aria-label="Close alert" className="absolute right-2 top-2 rounded p-1 hover:bg-black/10" onClick={onClose}>
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    )}
  </div>
);

const Fade = ({ children }) => <>{children}</>;
const Zoom = ({ children }) => <>{children}</>;
const Slide = ({ children }) => <>{children}</>;

const getProductSequence = (product) => {
  if (!product.optionalAttributes) return null;
  const sequenceAttr = product.optionalAttributes.find(attr => attr.name === 'sequence');
  if (!sequenceAttr || !sequenceAttr.values || sequenceAttr.values.length === 0) return null;
  const sequenceValue = sequenceAttr.values[0].value;
  return parseInt(sequenceValue, 10) || null;
};

const ProductRow = ({
  product,
  currency,
  isOrderItem,
  depth = 0,
  selectedProducts,
  onSelect,
  isChild = false,
  parentSelected = false,
  isMainProduct = false,
  nextExpectedSequence,
  isSequential
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = product.hasChild && product.childProducts?.length > 0;
  const isSelected = selectedProducts.includes(product.primaryId) || parentSelected;
  const theme = useTheme();
  const sequence = getProductSequence(product);

  const isSelectable = !isOrderItem &&
    (!isSequential ||
      (sequence !== null && sequence === nextExpectedSequence));

  const handleSelect = (e) => {
    e.stopPropagation();
    const newSelected = !isSelected;
    onSelect(product.primaryId, newSelected, hasChildren);
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const sortedOptionalAttributes = product.optionalAttributes
    ?.filter(attr => attr.name !== 'sequence')
    ?.map(attr => ({
      ...attr,
      values: [...attr.values].sort((a, b) => (a.count || 0) - (b.count || 0))
    })) || [];

  return (
    <>
      <TableRow
        hover
        sx={{
          backgroundColor: isChild ? theme.palette.grey[50] : 'inherit',
          '&:hover': { backgroundColor: theme.palette.action.hover },
          display: isMainProduct && hasChildren ? 'none' : 'table-row'
        }}
      >
        <TableCell sx={{ pl: depth * 4 }}>
          <Box display="flex" alignItems="center">
            {hasChildren && (
              <IconButton size="small" onClick={toggleExpand} sx={{ mr: 1 }}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
            {!hasChildren && <Box width={40} />}
            {!isOrderItem && (
              <Checkbox
                checked={isSelected}
                onChange={handleSelect}
                color="success"
                icon={<RadioButtonUnchecked />}
                checkedIcon={<CheckCircle />}
                onClick={(e) => e.stopPropagation()}
                disabled={!isSelectable && !isSelected}
                sx={{
                  '&.is-checked': {
                    color: theme.palette.success.main,
                  },
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            {product.imageURL ? (
              <Avatar
                src={product.imageURL}
                alt={product.name}
                sx={{ width: 40, height: 40, mr: 2 }}
              />
            ) : (
              <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 40, height: 40, mr: 2 }}>
                <School />
              </Avatar>
            )}
            <Box>
              <Typography variant="body1" fontWeight={isChild ? 'normal' : 'medium'}>
                {product.name}
              </Typography>
              {sortedOptionalAttributes.length > 0 && (
                <Box mt={1}>
                  {sortedOptionalAttributes.map((attr, idx) => (
                    <Box key={idx} display="flex" alignItems="center" flexWrap="wrap">
                      <Typography variant="caption" color="textSecondary" mr={1}>
                        {attr.name}:
                      </Typography>
                      {attr.values.map((val, valIdx) => (
                        <Chip
                          key={valIdx}
                          label={`${val.value} (${val.count || 0})`}
                          size="small"
                          sx={{
                            mr: 1,
                            mb: 1,
                            backgroundColor: theme.palette.grey[200],
                            color: theme.palette.text.primary,
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          {product.isOrderItem && product.orderStatusHistoryList?.length > 0 ? (
            <Box display="flex" alignItems="center">
              <CheckCircle fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
              <Typography variant="body2">
                {FormatDate(product.orderStatusHistoryList[0].eventDate)}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <CalendarToday fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
              <Typography variant="body2">
                {product.period ? `${FormatDate(product.period?.effectiveStartDate)} - ${FormatDate(product.period?.effectiveEndDate)}` : ''}
              </Typography>
            </Box>
          )}
          {product.schedule && (
            <Box display="flex" alignItems="center" mt={1}>
              <Schedule fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="textSecondary">
                {product.schedule.daysOfSchedule?.map((day, index) => (
                  <span key={index}>
                    {day.slice(0, 3)}
                    {index < product.schedule.daysOfSchedule.length - 1 ? ", " : ""}
                  </span>
                ))}
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body1" fontWeight="medium">
            {currency}{(product.price?.priceAmt * (product.quantity || 1)).toFixed(2)}
          </Typography>
        </TableCell>
      </TableRow>

      {hasChildren && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, borderBottom: 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <TableContainer component={Box} sx={{ backgroundColor: theme.palette.grey[50] }}>
                <Table size="small">
                  <TableBody>
                    {product.childProducts.map((child, index) => (
                      <ProductRow
                        key={index}
                        product={{ ...child, quantity: product.quantity, period: product.period, schedule: product.schedule }}
                        currency={currency}
                        isOrderItem={isOrderItem}
                        depth={depth + 1}
                        selectedProducts={selectedProducts}
                        onSelect={onSelect}
                        isChild={true}
                        parentSelected={isSelected}
                        nextExpectedSequence={nextExpectedSequence}
                        isSequential={isSequential}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const FeePayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const [cartData, setCartData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [mergedProducts, setMergedProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [, setActiveStep] = useState(0);
  const [isSequential, setIsSequential] = useState(false);
  const [nextExpectedSequence, setNextExpectedSequence] = useState(1);
  const userDetail = useSelector((state) => state?.auth?.data) || '';
  const applicantData = userDetail;
  const accountId = applicantData?.account?.primaryId || '';
  const shoppingCartId = applicantData?.account?.shoppingCart?.primaryId || '';
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const shoppId = userDetail?.account?.shoppingCart?.primaryId || '';
  const companyData = useSelector((state) => state.companyReducer.company);
  const companyPrimaryId = companyData?.primaryId;
  const currencySymbol = "₹";
  const fetchOrderDataRef = React.useRef(null);
  const getShoppingCartDataRef = React.useRef(null);

  useEffect(() => {
    const hasSequentialProducts = mergedProducts.some(
      product => getProductSequence(product) !== null && !product.isOrderItem
    );
    setIsSequential(hasSequentialProducts);
  }, [mergedProducts]);

  useEffect(() => {
    if (!isSequential) {
      setNextExpectedSequence(1);
      return;
    }

    const selectedSequences = selectedProducts.map(productId => {
      const product = mergedProducts.find(p => p.primaryId === productId);
      return getProductSequence(product) || 0;
    }).filter(seq => seq > 0);

    let expected = 1;
    for (let i = 0; i < selectedSequences.length; i++) {
      if (selectedSequences.includes(expected)) {
        expected++;
      } else {
        break;
      }
    }

    setNextExpectedSequence(expected);
  }, [selectedProducts, mergedProducts, isSequential]);

  useEffect(() => {
    const mergeProducts = () => {
      const merged = [];
      const processedProductIds = new Set();

      orderData.forEach(order => {
        order.lineItemList.forEach(orderItem => {
          if (orderItem.product && !processedProductIds.has(orderItem.product.primaryId)) {
            const productWithChildren = {
              ...orderItem.product,
              isOrderItem: true,
              orderStatus: "PAID",
              quantity: orderItem.quantity,
              parentOrderId: order.primaryId,
              hasChild: orderItem.product.childProducts?.length > 0,
              childProducts: orderItem.product.childProducts || [],
              orderStatusHistoryList: order.orderStatusHistoryList || []
            };
            merged.push(productWithChildren);
            processedProductIds.add(orderItem.product.primaryId);
          }
        });
      });

      cartData.forEach(cartItem => {
        const product = cartItem.scheduledProduct?.product || cartItem.product;
        if (product && !processedProductIds.has(product.primaryId)) {
          const productWithChildren = {
            ...product,
            isOrderItem: false,
            quantity: cartItem.quantity,
            period: cartItem.scheduledProduct?.period,
            schedule: cartItem.scheduledProduct?.schedule,
            cartItemId: cartItem.scheduledProduct?.primaryId || cartItem.product?.primaryId,
            hasChild: product.childProducts?.length > 0,
            childProducts: product.childProducts || []
          };
          merged.push(productWithChildren);
          processedProductIds.add(product.primaryId);
        }
      });

      return merged;
    };

    const merged = mergeProducts();
    setMergedProducts(merged);
  }, [cartData, orderData]);

  useEffect(() => {
    const filtered = mergedProducts.filter(product => {
      if (isSequential && !product.isOrderItem && getProductSequence(product) === null) {
        return false;
      }

      return !product.hasChild || product.isOrderItem ||
        (product.childProducts && product.childProducts.some(child =>
          mergedProducts.some(p => p.primaryId === child.primaryId)));
    });
    setFilteredProducts(filtered);
  }, [mergedProducts, isSequential]);

  useEffect(() => {
    if (!isAuthenticated) return;
    getShoppingCartDataRef.current?.(shoppId);
    fetchOrderDataRef.current?.();
  }, [isAuthenticated, shoppId]);

  useEffect(() => {
    if (shoppingCartId) {
      getShoppingCartDataRef.current?.(shoppingCartId);
    } else {
      setAlertMsg({
        open: true,
        message: "Applicant information not found",
        severity: "error"
      });
    }
  }, [shoppingCartId]);

  useEffect(() => {
    let total = 0;

    selectedProducts.forEach(productId => {
      const product = mergedProducts.find(p =>
        p.primaryId === productId ||
        (p.childProducts && p.childProducts.some(child => child.primaryId === productId))
      );

      if (product) {
        if (product.primaryId === productId) {
          total += (product.price?.priceAmt || 0) * (product.quantity || 1);
          if (product.childProducts) {
            product.childProducts.forEach(child => {
              total += (child.price?.priceAmt || 0) * (product.quantity || 1);
            });
          }
        } else {
          const child = product.childProducts.find(c => c.primaryId === productId);
          if (child) {
            total += (child.price?.priceAmt || 0) * (product.quantity || 1);
          }
        }
      }
    });

    setTotalAmount(total);
  }, [selectedProducts, mergedProducts]);

  const fetchOrderData = async () => {
    try {
      const persistedRoot = JSON.parse(localStorage.getItem("persist:root") || "{}");
      const persistedCompanyReducer = JSON.parse(persistedRoot?.companyReducer || "{}");
      const companyPrimaryId =
        companyData?.primaryId ||
        persistedCompanyReducer?.company?.primaryId ||
        localStorage.getItem("companyPrimaryId");
      const response = await dispatch(findOrders(accountId, companyPrimaryId));
      if (response) {
        setOrderData(response);
      }
    } catch {
      setAlertMsg({
        open: true,
        message: "Failed to fetch order data.",
        severity: "error",
      });
    }
  };
  fetchOrderDataRef.current = fetchOrderData;

  const handleProductSelect = useCallback((productId, isChecked, hasChildren) => {
    const product = mergedProducts.find(p => p.primaryId === productId);
    const sequence = getProductSequence(product);

    if (!isChecked) {
      setSelectedProducts(prev => {
        let newSelected = [...prev];

        // Remove the product
        newSelected = newSelected.filter(id => id !== productId);

        // If this product has children, remove them too
        if (hasChildren && product?.childProducts) {
          product.childProducts.forEach(child => {
            newSelected = newSelected.filter(id => id !== child.primaryId);
          });
        }

        // If this is a sequential product, remove all products with higher sequence numbers
        if (isSequential && sequence !== null) {
          mergedProducts.forEach(p => {
            const pSequence = getProductSequence(p);
            if (pSequence !== null && pSequence > sequence) {
              newSelected = newSelected.filter(id => id !== p.primaryId);
              // Also remove any children of these products
              if (p.childProducts) {
                p.childProducts.forEach(child => {
                  newSelected = newSelected.filter(id => id !== child.primaryId);
                });
              }
            }
          });
        }

        return newSelected;
      });
      return;
    }

    if (isSequential && sequence !== null) {
      if (sequence !== nextExpectedSequence) {
        return;
      }
    }

    setSelectedProducts(prev => {
      let newSelected = [...prev];
      if (!newSelected.includes(productId)) {
        newSelected.push(productId);
      }

      if (hasChildren && product?.childProducts) {
        product.childProducts.forEach(child => {
          if (!newSelected.includes(child.primaryId)) {
            newSelected.push(child.primaryId);
          }
        });
      }

      return newSelected;
    });
  }, [mergedProducts, isSequential, nextExpectedSequence]);

  useEffect(() => {
    if (!isSequential) {
      setNextExpectedSequence(1);
      return;
    }

    const purchasedSequences = mergedProducts
      .filter(p => p.isOrderItem)
      .map(p => getProductSequence(p))
      .filter(seq => seq !== null)
      .sort((a, b) => a - b);

    const selectedSequences = selectedProducts
      .map(productId => {
        const product = mergedProducts.find(p => p.primaryId === productId);
        return product && !product.isOrderItem ? getProductSequence(product) : null;
      })
      .filter(seq => seq !== null)
      .sort((a, b) => a - b);

    let expected = 1;

    for (const seq of purchasedSequences) {
      if (seq === expected) {
        expected++;
      } else if (seq > expected) {
        break;
      }
    }

    for (const seq of selectedSequences) {
      if (seq === expected) {
        expected++;
      } else if (seq > expected) {
        break;
      }
    }

    setNextExpectedSequence(expected);
  }, [selectedProducts, mergedProducts, isSequential]);

  const getShoppingCartData = async (cartId) => {
    setIsLoading(true);
    try {
      const data = await getShoppingCartItems(cartId);
      if (data) {
        setCartData(data[0].productList);
        localStorage.setItem("applicantCartProducts", JSON.stringify(data[0].productList));
      }
    } catch (error) {
      handleError("Failed to load cart data", error);
    } finally {
      setIsLoading(false);
    }
  };
  getShoppingCartDataRef.current = getShoppingCartData;

  const handleCheckout = async () => {
    try {
      if (selectedProducts.length === 0) {
        setAlertMsg({
          open: true,
          message: "Please select at least one product to proceed",
          severity: "warning"
        });
        return;
      }

      setLoading(true);
      setActiveStep(1);

      const lineItemList = [];

      selectedProducts.forEach(productId => {
        const product = mergedProducts.find(p =>
          p.primaryId === productId ||
          (p.childProducts && p.childProducts.some(child => child.primaryId === productId))
        );

        if (product) {
          if (product.primaryId === productId) {
            const mainPrice = product.price?.priceAmt || 0;
            const childTotalPrice = product.childProducts?.reduce((acc, child) => acc + (child.price?.priceAmt || 0), 0) || 0;
            const quantity = product.quantity || 1;
            const totalPrice = (mainPrice + childTotalPrice) * quantity;

            lineItemList.push({
              quantity,
              totalPrice,
              scheduledProduct: {
                primaryId: 7411,
                product: {
                  code: product.code,
                  name: product.name,
                  primaryId: product.primaryId,
                  price: {
                    priceAmt: product.price.priceAmt || 0,
                    base: product.price?.type || "BASE",
                  },
                },
              },
              isAssociatedProduct: false
            });

            if (product.childProducts) {
              product.childProducts.forEach(child => {
                lineItemList.push({
                  quantity,
                  totalPrice: 0,
                  scheduledProduct: {
                    primaryId: 7411,
                    product: {
                      code: child.code,
                      name: child.name,
                      primaryId: child.primaryId,
                      price: {
                        priceAmt: child.price.priceAmt || 0,
                        base: child.product.price?.type || "BASE",
                      },
                    },
                  },
                  isAssociatedProduct: true,
                  parentProductId: product.primaryId
                });
              });
            }
          } else {
            const child = product.childProducts.find(c => c.primaryId === productId);
            if (child && !selectedProducts.includes(product.primaryId)) {
              lineItemList.push({
                quantity: product.quantity || 1,
                totalPrice: (child.price?.priceAmt || 0) * (product.quantity || 1),
                product: {
                  code: child.code,
                  name: child.name,
                  primaryId: child.primaryId,
                },
                isAssociatedProduct: true,
                parentProductId: product.primaryId
              });
            }
          }
        }
      });

      const orderRequest = {
        accountId: accountId,
        company: {
          primaryId: companyPrimaryId,
          domain: {
            type: "INSTITUTION",
          },
        },
        createdBy: userDetail.firstName + " " + userDetail.lastName,
        deliveryType: "SHIPPING",
        totalLineItems: lineItemList.length,
        totalAmount: totalAmount,
        subtotalAmount: totalAmount,
        shippingAndHandlingAmount: 0,
        lineItemList,
        note: "Order created from cart"
      };

      const orderResponse = await createOrder(orderRequest);
      if (orderResponse) {
        dispatch(setOrder(orderResponse));
        localStorage.setItem("orderDetails", JSON.stringify(orderResponse));
        setActiveStep(2);
        navigate(`/order-history?amount=${orderResponse.dueAmount}`);
      }
    } catch (error) {
      handleError("Checkout failed", error);
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (message, error) => {
    setAlertMsg({
      open: true,
      message: `${message}: ${error.message || error}`,
      severity: "error",
    });
  };

  const handleCloseAlert = () => {
    setAlertMsg(prev => ({ ...prev, open: false }));
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <>
      {loading && <Loading message="Processing your order..." />}
      <br /><br /><br /><br />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="py-8 px-4 bg-gray-50 font-sans relative overflow-hidden min-h-screen"
        style={{ fontFamily: theme.fontFamily }}
      >
        <Container maxWidth="lg">
          <Box className="mb-8">
            <Button
              id="back-button"
              startIcon={<ArrowBackIosNew />}
              onClick={handleBackNavigation}
              className="normal-case"
              style={{
                textTransform: 'none',
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff'
                }
              }}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff'
                }
              }}
            >
              Back to Previous Page
            </Button>

            <Box className="flex items-center justify-between mt-4">
              <Typography id="fee-payment-portal" variant="h4" className="font-bold" sx={{ color: theme.palette.text.primary }}>
                <School sx={{ verticalAlign: 'middle', mr: 2, color: theme.palette.primary.main }} />
                Fee Payment Portal
              </Typography>
              {userDetail?.firstName && (
                <Typography variant="subtitle1" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                  {userDetail.firstName} {userDetail.lastName} • {userDetail.email}
                </Typography>
              )}
            </Box>
          </Box>

          <Backdrop open={isLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <TailSpin stroke={theme.palette.primary.main} speed={0.8} />
          </Backdrop>

          <Snackbar
            open={alertMsg.open}
            autoHideDuration={6000}
            onClose={handleCloseAlert}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            TransitionComponent={Slide}
          >
            <Alert
              elevation={6}
              onClose={handleCloseAlert}
              severity={alertMsg.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {alertMsg.message}
            </Alert>
          </Snackbar>

          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              {/* <TailSpin stroke={theme.palette.primary.main} speed={0.8} /> */}
              <Typography variant="body1" sx={{ mt: 2 }}>Loading your enrollment items...</Typography>
            </Box>
          ) : filteredProducts.length > 0 ? (
            <Grid container spacing={4}>

              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden'
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{
                        p: 3,
                        backgroundColor: theme.palette.primary.light,
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}>
                        <Typography id="enrollment-items-title" variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                          Your Enrollment Items
                        </Typography>
                      </Box>

                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                              <TableCell sx={{ width: 60 }}>Select</TableCell>
                              <TableCell>Program/Course</TableCell>
                              <TableCell>Status/Date</TableCell>
                              <TableCell align="right" sx={{ width: 150 }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredProducts.map((product, index) => (
                              <ProductRow
                                key={index}
                                product={product}
                                currency={currencySymbol}
                                isOrderItem={product.isOrderItem}
                                selectedProducts={selectedProducts}
                                onSelect={handleProductSelect}  // Make sure this prop name matches
                                isMainProduct={product.hasChild && !product.isOrderItem}
                                nextExpectedSequence={nextExpectedSequence}
                                isSequential={isSequential}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Fade in={true} timeout={500}>
                    <Card
                      id="payment-summary-card"
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'sticky',
                        top: 20
                      }}
                    >
                      <CardContent>
                        <Box sx={{
                          p: 2,
                          backgroundColor: theme.palette.primary.light,
                          borderRadius: 2,
                          mb: 3,
                          textAlign: 'center'
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                            <Receipt sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Payment Summary
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">Selected Items:</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {selectedProducts.length}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">Subtotal:</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {currencySymbol} {totalAmount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Total Amount:
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                              {currencySymbol} {totalAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>

                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            id="proceed-to-payment-button"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCheckout}
                            endIcon={<Payment />}
                            sx={{
                              py: 2,
                              borderRadius: 2,
                              fontSize: '1rem',
                              fontWeight: 600,
                              boxShadow: theme.shadows[3],
                              textTransform: 'none',
                              '&:hover': {
                                boxShadow: theme.shadows[6]
                              }
                            }}
                            disabled={loading || selectedProducts.length === 0}
                          >
                            {loading ? 'Processing...' : 'Proceed to Payment'}
                          </Button>
                        </motion.div>

                        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: theme.palette.text.secondary }}>
                          Secure payment processing powered by our institution
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Zoom in={true}>
                <Box>
                  <LibraryBooks sx={{
                    fontSize: 60,
                    color: theme.palette.grey[400],
                    mb: 3,
                    opacity: 0.7
                  }} />
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.secondary }}>
                    No Enrollment Items Found
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, maxWidth: 500, mx: 'auto' }}>
                    {isSequential ?
                      "Your cart doesn't contain any products with valid sequence numbers." :
                      "Your cart is currently empty. Please add programs or courses to proceed with payment."}
                  </Typography>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      id="browse-programs-button"
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => navigate("/applicant/products")}
                      sx={{
                        px: 4,
                        py: 2,
                        borderRadius: 2,
                        fontWeight: 600,
                        boxShadow: theme.shadows[2],
                        textTransform: 'none'
                      }}
                      startIcon={<School />}
                    >
                      Browse Programs
                    </Button>
                  </motion.div>
                </Box>
              </Zoom>
            </Box>
          )}
        </Container>
      </motion.div>
    </>
  );
};

export default FeePayment;
